import { type CommonPromoFieldsFragment, type GroupContentFieldsFragment } from '../../__generated__/graphql-client-types';
import { type ContentRichText, type General, type GeneralGroupContent } from '../../types/content.types';
import { type Category } from '../../types/search.types';

export const isBrandCategory = (category: Category): boolean => {
	return Boolean(category && category.breadcrumbs && category.breadcrumbs.length > 0 && category.breadcrumbs[0].name === 'Brands');
};

export const extractCategoryBrandName = (category: Category): string | null => {
	const brandName = isBrandCategory(category) && category.breadcrumbs && category.breadcrumbs.length > 1 && category.breadcrumbs[1].name;
	return brandName || null;
};

type PromoBannerPriorityHash = {
	topPrimary?: CommonPromoFieldsFragment;
	topSecondary?: CommonPromoFieldsFragment;
	bottomPrimary?: CommonPromoFieldsFragment;
	bottomSecondary?: CommonPromoFieldsFragment;
	topSecondaryCalc?: CommonPromoFieldsFragment;
};

const constructPriorityMap = {
	TOP: 'topPrimary',
	TOP_SECONDARY: 'topSecondary',
	BOTTOM: 'bottomPrimary',
	BOTTOM_SECONDARY: 'bottomSecondary',
	TOP_SECONDARY_CALC: 'topSecondaryCalc'
};

/**
 * Takes an array of category promos and returns them as an object keyed by
 * priority (saves us iterating the same array 4 times or mutating it 3 times)
 */
export const mapPromoBannersByPriority = (sharedCategoryPromos: CommonPromoFieldsFragment[]): PromoBannerPriorityHash => {
	const promos: { [key: string]: CommonPromoFieldsFragment } = {};
	for (const promo of sharedCategoryPromos) {
		promos[constructPriorityMap[promo.priority]] = promo;
	}
	return promos;
};

export const isContentRichText = (item: General): item is ContentRichText => {
	return item.__typename === 'ContentRichText';
};

export const isGeneralGroupContent = (item: General): item is GeneralGroupContent => {
	return item.__typename === 'GeneralGroupContent';
};

export const verifyGroupsHaveContent = (groups: GroupContentFieldsFragment[]) => {
	return groups.length && groups.some(({ title, items }) => title || items.length);
};

export const formatDefaultCategoryMeta = (
	categoryTitle: string,
	siteName: 'Build.com' | 'Ferguson Home' = 'Ferguson Home'
): { metaTitle: string; metaDescription: string } => {
	const LABELED_TITLE = `${categoryTitle} Products - Shop All ${categoryTitle} | ${siteName}`;
	const LABELED_DESCRIPTION = `Shop all ${categoryTitle} Products at ${siteName}. Free Shipping on most orders $49+. Find an expert curated selection of ${categoryTitle} Products at low prices.`;
	const DEFAULT_TITLE = `Shop All Products | ${siteName}`;
	const DEFAULT_DESCRIPTION = `Shop all Products at ${siteName}. Free Shipping on most orders $49+. Find an expert curated selection of products at low prices.`;

	const metaTitle = categoryTitle ? LABELED_TITLE : DEFAULT_TITLE;
	const metaDescription = categoryTitle ? LABELED_DESCRIPTION : DEFAULT_DESCRIPTION;

	return {
		metaTitle,
		metaDescription
	};
};
